import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { QRCodeSVG } from 'qrcode.react';
import { MessageCircle, QrCode, RefreshCw, AlertCircle, CheckCircle, MessageSquare, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhatsAppManager = () => {
  const [whatsappStatus, setWhatsappStatus] = useState({
    connected: false,
    qr_available: false,
    user_info: null,
    queue_length: 0
  });
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchWhatsappStatus = async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching WhatsApp status...');
      
      const response = await axios.get(`${API}/whatsapp/status`, {
        timeout: 10000 // 10 second timeout
      });
      
      console.log('📊 WhatsApp status response:', response.data);
      setWhatsappStatus(response.data);
      
      if (response.data.qr_available) {
        console.log('📱 QR code available, fetching image...');
        try {
          // Get QR code image with timeout
          const qrResponse = await axios.get(`${API}/whatsapp/qr`, {
            responseType: 'blob',
            timeout: 15000 // 15 second timeout for QR image
          });
          const qrUrl = URL.createObjectURL(qrResponse.data);
          setQrCodeUrl(qrUrl);
          console.log('✅ QR code loaded successfully');
        } catch (qrError) {
          console.error('❌ Error fetching QR image:', qrError);
          setError('QR code is being generated, please wait...');
        }
      } else {
        setQrCodeUrl(null);
        console.log('📱 QR code not available yet');
      }
      
      setError(null);
    } catch (error) {
      console.error('❌ Error fetching WhatsApp status:', error);
      setError(error.response?.data?.detail || 'Error fetching WhatsApp status');
    } finally {
      setLoading(false);
    }
  };

  const disconnectWhatsapp = async () => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/whatsapp/disconnect`);
      
      if (response.data.success) {
        alert('WhatsApp disconnected successfully');
        fetchWhatsappStatus(); // Refresh status
      } else {
        alert('Failed to disconnect WhatsApp');
      }
    } catch (error) {
      console.error('Error disconnecting WhatsApp:', error);
      alert('Error disconnecting WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const generateNewQR = async () => {
    try {
      setLoading(true);
      setError(null);
      setQrCodeUrl(null);
      
      // First disconnect if connected
      if (whatsappStatus.connected) {
        await axios.post(`${API}/whatsapp/disconnect`);
      }
      
      // Wait a moment then fetch new status
      setTimeout(() => {
        fetchWhatsappStatus();
      }, 2000);
      
    } catch (error) {
      console.error('Error generating new QR:', error);
      setError('Failed to generate new QR code');
    }
  };

  useEffect(() => {
    fetchWhatsappStatus();
    
    // Auto-refresh status every 5 seconds (faster for QR generation)
    const interval = setInterval(fetchWhatsappStatus, 5000);
    return () => {
      clearInterval(interval);
      if (qrCodeUrl) {
        URL.revokeObjectURL(qrCodeUrl);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-red-50">
      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">
            WhatsApp Web Connection
          </h1>
          <p className="text-gray-600 text-sm sm:text-base leading-relaxed">
            Connect your personal WhatsApp to send invoices and messages to your clients
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Status Card */}
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-green-600" />
                Connection Status
              </h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span>Status:</span>
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full mr-2 ${whatsappStatus.connected ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className={`font-semibold ${whatsappStatus.connected ? 'text-green-600' : 'text-red-600'}`}>
                      {whatsappStatus.connected ? 'Connected' : 'Disconnected'}
                    </span>
                  </div>
                </div>

                {whatsappStatus.connected && whatsappStatus.user_info && (
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="font-semibold text-green-800">Connected WhatsApp:</p>
                    <p className="text-green-700">{whatsappStatus.user_info.name}</p>
                    <p className="text-green-600">+{whatsappStatus.user_info.phone}</p>
                  </div>
                )}

                {whatsappStatus.queue_length > 0 && (
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-blue-700">
                      {whatsappStatus.queue_length} messages in queue
                    </p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button 
                    onClick={fetchWhatsappStatus} 
                    disabled={loading}
                    variant="outline"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh Status
                  </Button>
                  
                  {!whatsappStatus.connected && (
                    <Button 
                      onClick={generateNewQR}
                      disabled={loading}
                      variant="secondary"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Generate New QR
                    </Button>
                  )}
                  
                  {whatsappStatus.connected && (
                    <Button 
                      onClick={disconnectWhatsapp}
                      disabled={loading}
                      variant="destructive"
                    >
                      Disconnect
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* QR Code Card */}
          <Card className="shadow-lg">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center">
                <QrCode className="w-5 h-5 mr-2 text-blue-600" />
                Connect Your WhatsApp
              </h2>
              
              {whatsappStatus.connected ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <p className="text-green-600 font-semibold">WhatsApp Connected!</p>
                  <p className="text-gray-600 mt-2">Your WhatsApp is ready to send invoices</p>
                </div>
              ) : whatsappStatus.qr_available && qrCodeUrl ? (
                <div className="text-center">
                  <img 
                    src={qrCodeUrl} 
                    alt="WhatsApp QR Code" 
                    className="mx-auto mb-4 border border-gray-300 rounded-lg"
                    style={{ maxWidth: '250px', width: '100%' }}
                  />
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800 font-semibold mb-2">How to connect:</p>
                    <ol className="text-sm text-blue-700 text-left space-y-1">
                      <li>1. Open WhatsApp on your phone</li>
                      <li>2. Go to Settings → Linked Devices</li>
                      <li>3. Tap &quot;Link a Device&quot;</li>
                      <li>4. Scan this QR code</li>
                    </ol>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <AlertCircle className="w-16 h-16 text-orange-500 mx-auto mb-4" />
                  <p className="text-orange-600 font-semibold">Waiting for QR Code...</p>
                  <p className="text-gray-600 mt-2">Starting WhatsApp connection process</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Privacy Notice */}
        <Card className="shadow-lg mt-6">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-blue-600" />
              Privacy & Security
            </h2>
            <div className="bg-blue-50 p-4 rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h3 className="font-semibold text-blue-800 mb-2">Your WhatsApp, Your Messages</h3>
                  <ul className="space-y-1 text-blue-700">
                    <li>• Your WhatsApp connection is private to you</li>
                    <li>• Messages are sent from YOUR phone number</li>
                    <li>• Other users cannot access your WhatsApp</li>
                    <li>• You can disconnect at any time</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-blue-800 mb-2">Multi-Tenant Security</h3>
                  <ul className="space-y-1 text-blue-700">
                    <li>• Each user has isolated WhatsApp session</li>
                    <li>• No cross-user message contamination</li>
                    <li>• Your clients only receive from your number</li>
                    <li>• Complete privacy and professional identity</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default WhatsAppManager;
